import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AuthPermission } from 'api/generated/types/authPermission';
import type { AuthRole } from 'api/generated/types/authRole';
import type { AuthPermissionOverride } from 'api/generated/types/authPermissionOverride';
import { PermissionAction } from 'api/generated/types/permissionAction';
import { PermissionEntityType } from 'api/generated/types/permissionEntityType';
import { PermissionScope } from 'api/generated/types/permissionScope';

interface PermissionsState {
  // State
  role: AuthRole | null;
  permissions: AuthPermission[];
  permissionOverrides: AuthPermissionOverride[];
  isLoaded: boolean;
  // Map of campaign IDs to whether user can edit them (populated from campaigns response)
  editableCampaigns: Map<string, boolean>;
  // Map of campaign IDs to whether user can manage candidates (populated from campaigns response)
  manageCandidatesCampaigns: Map<string, boolean>;
  // Map of campaign IDs to whether user can pause/resume/auto-activate (populated from campaigns response)
  manageLifecycleCampaigns: Map<string, boolean>;

  // Actions
  setPermissions: (
    role: AuthRole | null | undefined,
    permissions: AuthPermission[] | undefined,
    permissionOverrides: AuthPermissionOverride[] | undefined
  ) => void;
  clearPermissions: () => void;
  /**
   * Replace all three campaign-access maps in a single Zustand notify.
   * Called after fetching / searching the campaigns list.
   */
  setCampaignAccessMaps: (maps: {
    editable: Record<string, boolean>;
    manageCandidates: Record<string, boolean>;
    manageLifecycle: Record<string, boolean>;
  }) => void;
  /**
   * Merge into all three campaign-access maps in a single Zustand notify.
   * Used when paginating (e.g. "load more"): a full replace would drop earlier
   * pages' permissions and silently fall back to org-wide gating.
   */
  mergeCampaignAccessMaps: (maps: {
    editable: Record<string, boolean>;
    manageCandidates: Record<string, boolean>;
    manageLifecycle: Record<string, boolean>;
  }) => void;
  setCampaignEditable: (campaignId: string, canEdit: boolean) => void;
  setCampaignManageCandidates: (
    campaignId: string,
    canManageCandidates: boolean
  ) => void;
  setCampaignManageLifecycle: (
    campaignId: string,
    canManageLifecycle: boolean
  ) => void;

  // Permission checkers
  hasPermission: (
    action: PermissionAction,
    entityType: PermissionEntityType,
    scope: PermissionScope
  ) => boolean;

  // Convenience methods for UI feature gating
  canViewAllJobs: () => boolean;
  canEditAllJobs: () => boolean;
  canViewAllFolders: () => boolean;
  canEditAllFolders: () => boolean;
  isAdmin: () => boolean;

  // Entity-specific permission checks (uses overrides)
  hasJobAccess: (jobId: string, action: PermissionAction) => boolean;
  hasManageCandidatesAccess: (jobId: string) => boolean;
  canManageJobLifecycle: (jobId: string) => boolean;
  canRestartWorkflow: (jobId: string) => boolean;
  hasFolderAccess: (folderId: string, action: PermissionAction) => boolean;
  /** Mirrors the backend job-creation rule; also gates duplicating a job into its folder. */
  canCreateJobIn: (folderId?: string | null) => boolean;
}

/**
 * Permissions store that holds role, permissions, and permission overrides from login response.
 *
 * This store is used for UI feature gating only. All real authorization is enforced
 * server-side via PermissionChecker.
 *
 * Resolution Order for entity-specific checks:
 * 1. Check permission overrides for entity-specific grant/deny
 * 2. Check role permissions for org-wide permission (scope: 'all')
 * 3. DENY
 */
// Marked pure (with devtools) so a build that imports this module only
// through the session store, and never uses the store, drops it (see
// useSessionStore).
export const usePermissionsStore = /* @__PURE__ */ create<PermissionsState>()(
  /* @__PURE__ */ devtools(
    (set, get) => ({
      role: null,
      permissions: [],
      permissionOverrides: [],
      isLoaded: false,
      editableCampaigns: new Map(),
      manageCandidatesCampaigns: new Map(),
      manageLifecycleCampaigns: new Map(),

      // Actions
      setPermissions: (role, permissions, permissionOverrides) => {
        set({
          role: role ?? null,
          permissions: permissions ?? [],
          permissionOverrides: permissionOverrides ?? [],
          isLoaded: true,
        });
      },

      clearPermissions: () => {
        set({
          role: null,
          permissions: [],
          permissionOverrides: [],
          isLoaded: false,
          editableCampaigns: new Map(),
          manageCandidatesCampaigns: new Map(),
          manageLifecycleCampaigns: new Map(),
        });
      },

      setCampaignAccessMaps: ({
        editable,
        manageCandidates,
        manageLifecycle,
      }) => {
        set({
          editableCampaigns: new Map(Object.entries(editable)),
          manageCandidatesCampaigns: new Map(Object.entries(manageCandidates)),
          manageLifecycleCampaigns: new Map(Object.entries(manageLifecycle)),
        });
      },

      mergeCampaignAccessMaps: ({
        editable,
        manageCandidates,
        manageLifecycle,
      }) => {
        const nextEditable = new Map(get().editableCampaigns);
        for (const [campaignId, canEdit] of Object.entries(editable)) {
          nextEditable.set(campaignId, canEdit);
        }
        const nextManageCandidates = new Map(get().manageCandidatesCampaigns);
        for (const [campaignId, canManage] of Object.entries(
          manageCandidates
        )) {
          nextManageCandidates.set(campaignId, canManage);
        }
        const nextManageLifecycle = new Map(get().manageLifecycleCampaigns);
        for (const [campaignId, canManage] of Object.entries(manageLifecycle)) {
          nextManageLifecycle.set(campaignId, canManage);
        }
        set({
          editableCampaigns: nextEditable,
          manageCandidatesCampaigns: nextManageCandidates,
          manageLifecycleCampaigns: nextManageLifecycle,
        });
      },

      setCampaignEditable: (campaignId: string, canEdit: boolean) => {
        const current = get().editableCampaigns;
        const next = new Map(current);
        next.set(campaignId, canEdit);
        set({ editableCampaigns: next });
      },

      setCampaignManageCandidates: (
        campaignId: string,
        canManageCandidates: boolean
      ) => {
        const current = get().manageCandidatesCampaigns;
        const next = new Map(current);
        next.set(campaignId, canManageCandidates);
        set({ manageCandidatesCampaigns: next });
      },

      setCampaignManageLifecycle: (
        campaignId: string,
        canManageLifecycle: boolean
      ) => {
        const current = get().manageLifecycleCampaigns;
        const next = new Map(current);
        next.set(campaignId, canManageLifecycle);
        set({ manageLifecycleCampaigns: next });
      },

      // Permission checkers
      hasPermission: (action, entityType, scope) => {
        const { permissions } = get();
        return permissions.some(
          (p) =>
            p.action === action &&
            p.entity_type === entityType &&
            p.scope === scope
        );
      },

      // Convenience methods for UI feature gating
      canViewAllJobs: () =>
        get().hasPermission(
          PermissionAction.view,
          PermissionEntityType.job,
          PermissionScope.all
        ),

      canEditAllJobs: () =>
        get().hasPermission(
          PermissionAction.edit,
          PermissionEntityType.job,
          PermissionScope.all
        ),

      canViewAllFolders: () =>
        get().hasPermission(
          PermissionAction.view,
          PermissionEntityType.folder,
          PermissionScope.all
        ),

      canEditAllFolders: () =>
        get().hasPermission(
          PermissionAction.edit,
          PermissionEntityType.folder,
          PermissionScope.all
        ),

      isAdmin: () => get().role?.key === 'admin',

      // Entity-specific permission checks
      hasJobAccess: (jobId, action) => {
        const state = get();

        // For EDIT actions, check the editableCampaigns map first (populated from campaigns response)
        // This includes "own" scope permissions which are computed server-side
        if (action === PermissionAction.edit) {
          const canEdit = state.editableCampaigns.get(jobId);
          if (canEdit !== undefined) {
            return canEdit;
          }
        }

        // Check for entity-specific override
        const override = state.permissionOverrides.find(
          (o) =>
            o.entity_id === jobId &&
            o.entity_type === PermissionEntityType.job &&
            o.action === action
        );

        if (override !== undefined) {
          return override.granted;
        }

        // Fall back to org-wide permission
        return state.hasPermission(
          action,
          PermissionEntityType.job,
          PermissionScope.all
        );
      },

      hasManageCandidatesAccess: (jobId: string) => {
        const state = get();
        const canManage = state.manageCandidatesCampaigns.get(jobId);
        if (canManage !== undefined) {
          return canManage;
        }

        const override = state.permissionOverrides.find(
          (o) =>
            o.entity_id === jobId &&
            o.entity_type === PermissionEntityType.candidate &&
            o.action === PermissionAction.edit
        );

        if (override !== undefined) {
          return override.granted;
        }

        return state.hasPermission(
          PermissionAction.edit,
          PermissionEntityType.candidate,
          PermissionScope.all
        );
      },

      canManageJobLifecycle: (jobId: string) => {
        const state = get();
        const canManage = state.manageLifecycleCampaigns.get(jobId);
        if (canManage !== undefined) {
          return canManage;
        }

        const override = state.permissionOverrides.find(
          (o) =>
            o.entity_id === jobId &&
            o.entity_type === PermissionEntityType.job &&
            o.action === PermissionAction.manage_lifecycle
        );

        if (override !== undefined) {
          return override.granted;
        }

        return state.hasPermission(
          PermissionAction.manage_lifecycle,
          PermissionEntityType.job,
          PermissionScope.all
        );
      },

      // Restart workflow is manage_lifecycle on the candidate entity. It has
      // no own scope and no folder grant, so the job override and the
      // org-wide permission fully decide it here.
      canRestartWorkflow: (jobId: string) => {
        const state = get();
        const override = state.permissionOverrides.find(
          (o) =>
            o.entity_id === jobId &&
            o.entity_type === PermissionEntityType.candidate &&
            o.action === PermissionAction.manage_lifecycle
        );

        if (override !== undefined) {
          return override.granted;
        }

        return state.hasPermission(
          PermissionAction.manage_lifecycle,
          PermissionEntityType.candidate,
          PermissionScope.all
        );
      },

      canCreateJobIn: (folderId) =>
        get().canEditAllJobs() ||
        (!!folderId && get().hasFolderAccess(folderId, PermissionAction.edit)),

      hasFolderAccess: (folderId, action) => {
        const state = get();

        // Check for entity-specific override first
        const override = state.permissionOverrides.find(
          (o) =>
            o.entity_id === folderId &&
            o.entity_type === PermissionEntityType.folder &&
            o.action === action
        );

        if (override !== undefined) {
          return override.granted;
        }

        // Fall back to org-wide permission
        return state.hasPermission(
          action,
          PermissionEntityType.folder,
          PermissionScope.all
        );
      },
    }),
    { name: 'permissions-store' }
  )
);
